import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import {
  createTicketStatus,
  deleteTicketStatus,
  listTicketStatuses,
  patchTicketStatus,
  reorderTicketStatuses,
  type TicketStatusRecord,
  type TicketStatusStage,
} from '@/lib/api/ticket-statuses.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { usePermissions } from '@/hooks/usePermissions.js';
import { canEditSettingsSection } from '@/lib/settings-access.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';

const STAGES: { value: TicketStatusStage; label: string }[] = [
  { value: 'intake', label: 'Intake' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'ready', label: 'Ready' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STAGE_HELP =
  'Automation (kanban, SLA, SMS, reports, derived status) reads STAGE — not the display name.';

export function TicketStatusesSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { flags } = usePermissions();
  const canEdit = canEditSettingsSection(flags, user?.role ?? 'read_only', 'tickets');

  const listQuery = useQuery({
    queryKey: ['ticket-statuses', 'all'],
    queryFn: () => listTicketStatuses({ includeInactive: true }),
  });

  const [draftName, setDraftName] = useState('');
  const [draftStage, setDraftStage] = useState<TicketStatusStage>('waiting');
  const [draftColor, setDraftColor] = useState('#6B7280');
  const [dragId, setDragId] = useState<string | null>(null);

  const statuses = listQuery.data ?? [];
  const ordered = useMemo(
    () => [...statuses].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [statuses],
  );

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['ticket-statuses'] });
  }

  const createMut = useMutation({
    mutationFn: () =>
      createTicketStatus({
        name: draftName.trim(),
        stage: draftStage,
        color: draftColor,
      }),
    onSuccess: () => {
      toastSuccess('Status created');
      setDraftName('');
      invalidate();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const patchMut = useMutation({
    mutationFn: (args: { id: string; body: Parameters<typeof patchTicketStatus>[1] }) =>
      patchTicketStatus(args.id, args.body),
    onSuccess: () => {
      toastSuccess('Status updated');
      invalidate();
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      if (parsed.code === 'STAGE_CHANGE_WARNING') {
        const ok = window.confirm(
          `${parsed.message}\n\nThis affects every ticket currently in that status.`,
        );
        if (!ok) return;
        const last = patchMut.variables;
        if (last) {
          patchMut.mutate({
            id: last.id,
            body: { ...last.body, acknowledgeStageChange: true },
          });
        }
        return;
      }
      toastError(parsed.message, parsed.code);
    },
  });

  const reorderMut = useMutation({
    mutationFn: (ids: string[]) => reorderTicketStatuses(ids),
    onSuccess: () => invalidate(),
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTicketStatus(id),
    onSuccess: () => {
      toastSuccess('Status deleted');
      invalidate();
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      if (parsed.code === 'STATUS_IN_USE') {
        toastError(parsed.message);
        return;
      }
      toastError(parsed.message, parsed.code);
    },
  });

  function onDrop(targetId: string) {
    if (!dragId || dragId === targetId || !canEdit) return;
    const ids = ordered.map((s) => s.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, dragId);
    setDragId(null);
    reorderMut.mutate(ids);
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-950">Ticket Statuses</h2>
        <p className="mt-1 text-sm text-slate-600">{STAGE_HELP}</p>
        <p className="mt-2 text-xs text-slate-500">
          <strong>tickets.status</strong> stores the status <em>slug</em> (not a UUID FK). System
          rows are seeded from the former enum; existing tickets are unchanged.
        </p>
      </div>

      {canEdit ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">
            Add custom status
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Name</span>
              <input
                className="mt-1 block w-48 rounded-xl border border-slate-300 px-3 py-2 text-sm"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder="Awaiting Customer Callback"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Stage</span>
              <select
                className="mt-1 block rounded-xl border border-slate-300 px-3 py-2 text-sm"
                value={draftStage}
                onChange={(e) => setDraftStage(e.target.value as TicketStatusStage)}
              >
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Color</span>
              <input
                type="color"
                className="mt-1 block h-10 w-14 cursor-pointer rounded border border-slate-300"
                value={draftColor}
                onChange={(e) => setDraftColor(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={!draftName.trim() || createMut.isPending}
              className="inline-flex items-center gap-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => createMut.mutate()}
            >
              <Plus className="h-4 w-4" />
              Create
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <ul className="divide-y divide-slate-100">
          {ordered.map((status) => (
            <StatusRow
              key={status.id}
              status={status}
              canEdit={canEdit}
              dragging={dragId === status.id}
              onDragStart={() => setDragId(status.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(status.id)}
              onPatch={(body) => patchMut.mutate({ id: status.id, body })}
              onDelete={() => {
                if (status.isSystem) return;
                if (!window.confirm(`Delete “${status.name}”?`)) return;
                deleteMut.mutate(status.id);
              }}
              onDeactivate={() =>
                patchMut.mutate({ id: status.id, body: { active: !status.active } })
              }
            />
          ))}
          {ordered.length === 0 && !listQuery.isLoading ? (
            <li className="px-6 py-8 text-center text-sm text-slate-500">No statuses yet.</li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}

function StatusRow({
  status,
  canEdit,
  dragging,
  onDragStart,
  onDragOver,
  onDrop,
  onPatch,
  onDelete,
  onDeactivate,
}: {
  status: TicketStatusRecord;
  canEdit: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
  onPatch: (body: Parameters<typeof patchTicketStatus>[1]) => void;
  onDelete: () => void;
  onDeactivate: () => void;
}) {
  const [name, setName] = useState(status.name);
  const [color, setColor] = useState(status.color);
  const [stage, setStage] = useState(status.stage);
  const [triggersSms, setTriggersSms] = useState(status.triggersSms);
  const [smsTemplate, setSmsTemplate] = useState(status.smsTemplate ?? '');

  return (
    <li
      className={cn('px-4 py-4 sm:px-6', dragging && 'bg-slate-50', !status.active && 'opacity-60')}
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="flex flex-wrap items-start gap-3">
        {canEdit ? (
          <button type="button" className="mt-2 cursor-grab text-slate-400" aria-label="Drag to reorder">
            <GripVertical className="h-5 w-5" />
          </button>
        ) : null}
        <input
          type="color"
          disabled={!canEdit}
          className="mt-1 h-9 w-10 rounded border border-slate-300"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          onBlur={() => {
            if (color !== status.color) onPatch({ color });
          }}
        />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-semibold"
              value={name}
              disabled={!canEdit}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                if (name.trim() && name.trim() !== status.name) onPatch({ name: name.trim() });
              }}
            />
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600">
              {status.slug}
            </span>
            {status.isSystem ? (
              <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase text-white">
                System
              </span>
            ) : null}
            {!status.active ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900">
                Inactive
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-semibold text-slate-600">
              Stage{' '}
              <select
                className="ml-1 rounded-lg border border-slate-300 px-2 py-1 text-sm"
                value={stage}
                disabled={!canEdit}
                onChange={(e) => {
                  const next = e.target.value as TicketStatusStage;
                  setStage(next);
                  onPatch({ stage: next });
                }}
              >
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600">
              <input
                type="checkbox"
                checked={triggersSms}
                disabled={!canEdit}
                onChange={(e) => {
                  setTriggersSms(e.target.checked);
                  onPatch({ triggersSms: e.target.checked });
                }}
              />
              SMS on enter
            </label>
          </div>
          {triggersSms ? (
            <textarea
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              rows={2}
              placeholder="SMS template (optional)"
              value={smsTemplate}
              disabled={!canEdit}
              onChange={(e) => setSmsTemplate(e.target.value)}
              onBlur={() => {
                if (smsTemplate !== (status.smsTemplate ?? '')) {
                  onPatch({ smsTemplate: smsTemplate || null });
                }
              }}
            />
          ) : null}
        </div>
        {canEdit ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700"
              onClick={onDeactivate}
            >
              {status.active ? 'Deactivate' : 'Activate'}
            </button>
            {!status.isSystem ? (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
